import { useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router"
import { Box, Link, Stack } from "@mui/material"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { FULL_SCREEN_BTN } from "minimals-template/components/editor/editor-buttons"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { Bound } from "lib/@components/binding/Bound"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { TreeToolbar } from "slot-definitions"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { SummaryDataChanged, TaskListRefresh, FieldUpdate } from "event-definitions"
import { indexAuthoringTasksInSchedule } from "./lib/index-authoring-tasks-in-schedule"
import { useProjectOnce } from "authoring/lib/hooks/use-project"
import { navigateProject, navigateScheduleEditor, navigateSchedulePreview } from "../utilities/project-navigation"
import { saveScheduleDraft } from "./controller/save-schedule-draft"
import { ScheduleEditorForm } from "./schedule-editor-form"
import { ScheduleEditorTasks } from "./schedule-editor-tasks"
import { RightBarTabs } from "routes/schedule/plugins/right-bar/right-bar-tabs"
import ScheduleDisplay from "routes/schedule/scheduleDisplay"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useCompareSchedule } from "./lib/use-compare-schedule"
import { SaveIndicator } from "lib/@components/save-indicator/save-indicator"
import { SaveWarning } from "routes/schedule/edit/save-warning"
import { AuthoringHeaderBarRight } from "authoring/routes/@layouts/authoring-app-layout"
import { useScheduleEditorToolbarPlugs } from "./hooks/use-schedule-editor-toolbar-plugs"
import { useScheduleEditorRightBarPlugs } from "./hooks/use-schedule-editor-rightbar-plugs"
import { SubmitForReview } from "./plugins/review/submit-for-review-button"
import { ReviewCommentaryButton } from "./plugins/review-commentary/review-commentary-button"
import { isEnabled } from "lib/@components/feature"
import { FEATURE_FLAG_SCHEDULE_COMMENTARY } from "authoring/lib/enum/authoring-feature-flags.mjs"
import { ScheduleEditorAmendsBoundary } from "./schedule-editor-amends-boundary"
import { SAVE_PREP_FORCE_FULL_SCOPE } from "./lib/save-prep-scopes"
import { isScheduleSavePerfEnabled, logScheduleSavePerf } from "./lib/schedule-save-perf"
import { useAsyncPreparedSavePayload } from "./lib/use-async-prepared-save-payload"
import {
    getAuthoringScheduleWorkerClient,
    releaseAuthoringScheduleWorkerClient,
} from "./lib/authoring-schedule-worker-client"
import { getMainThreadAuthoringNormalizers } from "./lib/authoring-schedule-compute-core"
import { resolveHasCompareChanges } from "authoring/routes/project/scoped-schedules/lib/resolve-has-compare-changes"
import { useDraftSchedule } from "./lib/use-draft-schedule"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"
import { ConditionalRedirect } from "lib/@components/conditional-redirect"
import { Redirect } from "lib/@components/redirect"
import { useDraftAmends } from "./lib/use-draft-amends"
import { useSave } from "lib/@hooks/use-save"
import "lib/@hooks/useEvent"
import { SaveSkippedUnchanged, ToBeSaved } from "lib/@components/save-indicator/to-be-saved"
import { useEnrichedMacroMapOnce } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import { ScheduleMacroAutocomplete } from "./schedule-macro-autocomplete"
import { useChipExpander } from "authoring/routes/macro/hooks/use-chip-expander"
import {
    collectDraftTokenIdSet,
    findUnknownMacroTokens,
    formatUnknownMacroMessage,
    createSaveConflictError,
} from "./lib/schedule-editor-utils"
import {
    SCHEDULE_EDITOR_ACTION_DELETE_TASK,
    SCHEDULE_EDITOR_ACTION_RESTORE_DRAFT_SNAPSHOT,
    SCHEDULE_EDITOR_ACTION_RESTORE_RECOMMENDATION_APPLICATION,
    SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_TASK_WITH_AMENDS,
    SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_LEGISLATION,
} from "./lib/schedule-editor-action-types"
import { RECOMMENDATION_AMEND_UNDO_DELETE, RECOMMENDATION_AMEND_UNDO_RESTORE } from "./lib/recommendation-amend-undo"
import { MacroTokenValidationFailed } from "authoring/routes/macro/macro-events"
import { MacroTokenWarningBanner } from "./macro-token-warning-banner"
import { buildScheduleForEditing } from "authoring/routes/macro/lib/macro-conversion"
import { getExpandedSchedulePreview } from "./controller/get-expanded-schedule-preview"
import { useScheduleEditorSession } from "./hooks/use-schedule-editor-session"
import { RejectionAlertPlug } from "./rejection-alert-plug"
import { createSlot } from "lib/@components/slot/create-slot"
import { OpenAmendFromUrl } from "./open-amend-from-url"
import { isLockedToLegacy } from "authoring/lib/enum/project-phase.mjs"
import { restoreDraftSnapshotInPlace } from "./lib/restore-draft-snapshot"
import { resolveScheduleRightBarWidth } from "routes/schedule/lib/right-bar-layout-presenters"
import { getScheduleDraft } from "./controller/get-schedule-draft"
import { persistProjectBridgeAppliedTargets } from "./lib/project-bridge-applied-recommendations"
import {
    AmendsChanged,
    RestorePointAdded,
    SaveSucceeded,
    DraftReloaded,
    DraftStructureChanged,
    DraftEdited,
    SubstantiveEditsChanged,
    SaveDirtyChanged,
} from "./schedule-editor-event-definitions.mjs"
import {
    fieldPathFromFieldUpdateEvent,
    resolveEditScopesFromFieldUpdate,
} from "./lib/authoring-recommendation-edit-scopes"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { cancelIdle, scheduleIdle } from "lib/schedule-idle-callback"
import { taskMatchesIdentifier } from "./lib/task-matches-identifier.mjs"
import { useDemand } from "lib/authorization/use-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"
import { AuthoringRecommendationSummaryBound } from "./authoring-recommendation-summary-bound"

export const AuthoringScheduleEditorAlert = createSlot("AuthoringScheduleEditorAlert")

const DRAFT_EDIT_DEBOUNCE_MS = 400

function withEventName(fn) {
    return function eventCapture(...args) {
        return fn(this?.event, ...args)
    }
}

export function ScheduleEditor({ preview = false }) {
    const { projectId, scheduleId } = useParams()
    const { project, loading: projectLoading } = useProjectOnce({ id: projectId })
    const { draft: initialDraft, loading: draftLoading } = useDraftSchedule({
        projectId,
        scheduleId,
    })
    const {
        amends,
        persistedAmends,
        inheritedAmends,
        saveAmend,
        deleteAmend,
        refreshAmends,
        initialLoading: amendsInitialLoading,
    } = useDraftAmends({
        projectId,
        scheduleId,
        flushDraftBeforeSave,
        seedSource: initialDraft?.seedSource,
    })
    const editorDraftId = createDraftId(projectId, scheduleId)
    const isNewSchedule = initialDraft?.seedSource === SeedSource.NEW
    const { loading: compareLoading, schedule: compareSource } = useCompareSchedule(
        scheduleId,
        "no_v",
        !!initialDraft && !isNewSchedule
    )
    const allAmends = [...persistedAmends, ...inheritedAmends]

    const [draft, setDraft] = useState(null)
    const [originalTasks, setOriginalTasks] = useState([])
    const [saveReady, setSaveReady] = useState(false)
    const [undoStack, setUndoStack] = useState([])

    const draftTokenIdsStateRef = useRef({ key: "", tokenIdSet: new Set(), tokenIds: [] })
    const amendsWithKeys = amends?.filter((amend) => amend?.payload?.amendsKey)
    const nextDraftTokenIdSet = collectDraftTokenIdSet(initialDraft, draft, amendsWithKeys, allAmends)
    const nextDraftTokenIds = [...nextDraftTokenIdSet]
    const nextDraftTokenIdsKey = nextDraftTokenIds.join("|")
    if (draftTokenIdsStateRef.current.key !== nextDraftTokenIdsKey) {
        draftTokenIdsStateRef.current = {
            key: nextDraftTokenIdsKey,
            tokenIdSet: nextDraftTokenIdSet,
            tokenIds: nextDraftTokenIds,
        }
    }
    const draftTokenIdSet = draftTokenIdsStateRef.current.tokenIdSet
    const draftTokenIds = draftTokenIdsStateRef.current.tokenIds
    const {
        macros: allMacros,
        macroMap,
        loading: macrosLoading,
    } = useEnrichedMacroMapOnce({
        tokenIds: draftTokenIds,
        retrieveDeleted: false,
    })
    const { handleChipClick, chipExpanderPopover } = useChipExpander({ macroMap })

    const draftRef = useRef(null)
    const undoStackRef = useRef([])
    const hasSubstantiveEditsRef = useRef(null)
    const isSaveDirtyRef = useRef(false)
    const substantiveEditsResyncTimerRef = useRef(null)
    const undoRestorePendingRef = useRef(false)
    const pendingEditScopesRef = useRef(new Set())
    const savePrepScopesRef = useRef(new Set())
    const draftEditDebounceRef = useRef(null)
    const sessionRef = useRef({})
    const saveConflictRef = useRef({ macrosLoading: false, invalidTokensKey: "" })
    const workerClientRef = useRef(null)
    const compareRequestRef = useRef(0)
    const substantiveCompareIdleRef = useRef(null)
    const runSubstantiveEditsCompareRef = useRef(null)
    const lastChangeAtRef = useRef(null)
    const componentTokenTargetRef = useRef(null)
    const compareStateRef = useRef({ compareLoading, compareSource })
    const macroStateRef = useRef({ macrosLoading, macroMap })
    const previewRef = useRef(preview)
    compareStateRef.current = { compareLoading, compareSource }
    macroStateRef.current = { macrosLoading, macroMap }
    previewRef.current = preview
    runSubstantiveEditsCompareRef.current = runSubstantiveEditsCompareFromIdle
    const canEditSchedule = useDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    if (!workerClientRef.current) {
        workerClientRef.current = getAuthoringScheduleWorkerClient({ draftId: editorDraftId })
    }
    const { markDirty, schedulePrep, consumePayload, releaseLock } = useAsyncPreparedSavePayload({
        workerClient: workerClientRef.current,
        getPrepParams: getSavePrepParams,
    })
    const rawOnChange = useSave({ save: saveDraft, target: draft, id: "schedule", wait: 3000, deferDirtyRetry: true }, [
        preview,
        projectId,
        scheduleId,
    ])
    const onChange = useMemo(wrapOnChangeWithPrep, [rawOnChange, markDirty, schedulePrep, projectId, scheduleId])
    useBoundContext({ onChange })
    const refresh = useRefresh()
    const refreshDraftUi = useRefresh(noChange)
    const macroScopeId = `${projectId}:${scheduleId}`
    const hasDraft = Boolean(draft)

    // In preview mode, fetch a server-expanded schedule so that component-linked
    // fields (componentRef / linkedFields) are resolved before macro tokens are
    // expanded. Pass undefined when not in preview so the query is skipped.
    const expandedPreview = getExpandedSchedulePreview.useResultsOnce({
        projectId: preview ? projectId : undefined,
        scheduleId: preview ? scheduleId : undefined,
    })

    const displayDraft = useMemo(resolveDisplayDraft, [draft, preview, expandedPreview])
    const rightBarWidth = resolveScheduleRightBarWidth({ isAuthoringScheduleEditor: true })

    draftRef.current = draft
    undoStackRef.current = undoStack
    sessionRef.current = {
        persistedAmends,
        inheritedAmends,
        authoringAmends: amends,
        saveAmend,
        deleteAmend,
        refreshAmends,
        flushDraftBeforeSave,
        pushUndo,
        reloadAuthoringDraftFromServer,
        draftTokenIdSet,
        draftTokenIds,
        macroMap,
        originalTasks,
        publishedTasks: compareSource?.tasks,
        compareSource,
        compareLoading,
        amendsInitialLoading,
        seedSource: initialDraft?.seedSource,
        initialIsDraft: Boolean(initialDraft?.isDraft),
        reviewCycle: draft?.reviewCycle,
        onComponentTokenInserted: handleComponentTokenInserted,
        onRegisterComponentTokenTarget: registerComponentTokenTarget,
        onClearComponentTokenTarget: clearComponentTokenTarget,
        notifyDraftEdited,
        notifyDraftStructureChanged,
        hasSubstantiveEdits: hasSubstantiveEditsRef.current,
        isSaveDirty: isSaveDirtyRef.current,
    }
    useScheduleEditorSession({ projectId, scheduleId, sessionRef, draftTokenIdsKey: nextDraftTokenIdsKey })

    ToBeSaved("schedule").useEvent(handleSaveDirtyChanged, [editorDraftId])

    FieldUpdate("**").useEvent(withEventName(handleFieldUpdateForDraftEdited), [preview, projectId, scheduleId])

    SaveSkippedUnchanged("schedule").useEvent(handleSaveSkippedForSubstantiveEdits)
    DraftReloaded(editorDraftId).useEvent(handleDraftReloadedForSubstantiveEdits)
    useEffect(cleanupWorkerClient, [editorDraftId])
    useEffect(warmWorkerWhenCompareSourceReady, [compareSource, compareLoading, isNewSchedule, editorDraftId])
    useEffect(scheduleSubstantiveEditsCompareOnIdle, [
        hasDraft,
        compareLoading,
        compareSource,
        macrosLoading,
        isNewSchedule,
        draftTokenIds.length,
    ])

    const isSubmitted = draft?.status === DraftStatus.SUBMITTED
    const isApproved = draft?.status === DraftStatus.APPROVED
    const isLocked = isSubmitted || isApproved

    useEffect(initializeDraft, [draft, draftTokenIds.length, initialDraft, macroMap, macrosLoading])
    useEffect(enableSaving, [draft, preview])

    useScheduleEditorToolbarPlugs({
        preview,
        hasUndo: undoStack.length > 0,
        projectId,
        scheduleId,
        isNewSchedule,
        onPreview,
        onEdit,
        onUndo: handleUndo,
        isLocked,
    })

    TreeToolbar.usePlug(<SubmitForReview priority={70} flushSave={onChange.flush} if={() => !preview && !isLocked} />, [
        preview,
        isLocked,
        onChange.flush,
    ])

    TreeToolbar.usePlug(
        <ReviewCommentaryButton
            priority={71}
            if={() => preview && isApproved && isEnabled(FEATURE_FLAG_SCHEDULE_COMMENTARY)}
        />,
        [preview, isApproved]
    )

    useScheduleEditorRightBarPlugs()

    if (projectLoading && !project) return <LoadingScreen />

    const lockedRedirectPath = `/authoring/projects/${projectId}`
    const scopedScheduleIds = project?.scopedScheduleIds ?? []
    if (!scopedScheduleIds.includes(scheduleId)) return <Redirect to={lockedRedirectPath} replace />

    if (draftLoading && !initialDraft) return <LoadingScreen />
    if (!draft && !initialDraft) return <LoadingScreen />
    if (!displayDraft) return <LoadingScreen />

    const sentToLegacy = isLockedToLegacy(project?.phase)
    const shouldRedirectWhenLocked = !preview && (isLocked || sentToLegacy)
    const shouldRedirectReadonlyEditRoute = !preview && !isLocked && !sentToLegacy && !canEditSchedule
    const readonlyRedirectPath = `/authoring/projects/${projectId}/schedule-editor/${scheduleId}/preview`
    let redirectPath = null
    if (shouldRedirectWhenLocked) {
        redirectPath = lockedRedirectPath
    } else if (shouldRedirectReadonlyEditRoute) {
        redirectPath = readonlyRedirectPath
    }
    const editMode = !preview && !isLocked && canEditSchedule
    const parentCrumbs = [
        <Link key="project" sx={{ cursor: "pointer" }} onClick={() => navigateProject(projectId)}>
            {project?.name}
        </Link>,
    ]

    return (
        <ConditionalRedirect when={!!redirectPath} to={redirectPath}>
            <Bound
                target={displayDraft}
                rawSchedule={draft}
                schedule={displayDraft}
                isPreview={preview}
                isAuthoringScheduleEditor={true}
                editorAddBtns={[FULL_SCREEN_BTN]}
                refresh={preview || !canEditSchedule ? undefined : refresh}
                hideUpBar={preview}
                hideSelected={preview}
                onChange={preview || !saveReady || !canEditSchedule ? undefined : onChange}
            >
                <PageBreadcrumbContainer
                    tightWidth="xl"
                    title={`${preview ? "Preview" : "Edit"}: ${displayDraft.code}`}
                    parentCrumbs={parentCrumbs}
                >
                    <Bound editMode={editMode}>
                        <RejectionAlertPlug />
                        <If
                            not
                            truthy={preview}
                            then={<SaveIndicator id="schedule" slot={AuthoringHeaderBarRight} />}
                        />
                        <If
                            not
                            truthy={preview}
                            then={<SaveWarning id="schedule" onRetry={handleRetrySave} slot={null} />}
                        />
                        <AuthoringScheduleEditorAlert.Slot />
                        <If value={preview}>
                            <ScheduleDisplay />
                            <Else>
                                <Bound macroMap={macroMap}>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        sx={{ height: "100%", flex: 1, minHeight: 0, pt: 2 }}
                                    >
                                        <Box flex={1} sx={{ overflowY: "auto" }} onClickCapture={handleChipClick}>
                                            <ScheduleEditorAmendsBoundary>
                                                <OpenAmendFromUrl />
                                                <MacroTokenWarningBanner schedule={draft} scopeId={macroScopeId} />
                                                <ScheduleEditorForm />
                                                <ScheduleEditorTasks />
                                            </ScheduleEditorAmendsBoundary>
                                        </Box>
                                        <Box
                                            sx={{
                                                width: rightBarWidth.width,
                                                flexShrink: 0,
                                                minWidth: rightBarWidth.minWidth,
                                                maxWidth: rightBarWidth.maxWidth,
                                                minHeight: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                                overflowY: "hidden",
                                            }}
                                        >
                                            <AuthoringRecommendationSummaryBound>
                                                <RightBarTabs />
                                            </AuthoringRecommendationSummaryBound>
                                        </Box>
                                    </Stack>
                                </Bound>
                            </Else>
                        </If>
                        <If truthy={!preview}>
                            <ScheduleMacroAutocomplete macros={allMacros} enabled={!isLocked} />
                        </If>
                    </Bound>
                </PageBreadcrumbContainer>
                {chipExpanderPopover}
            </Bound>
        </ConditionalRedirect>
    )

    function resolveDisplayDraft() {
        if (!draft) return null
        if (preview) {
            // Server resolves component-linked fields then expands macro tokens.
            // Return null while loading so <LoadingScreen /> shows.
            return expandedPreview?.schedule ?? null
        }
        return draft
    }

    function initializeDraft() {
        if (!initialDraft || draft || (macrosLoading && draftTokenIds.length > 0)) return
        const cloned = structuredClone(initialDraft)
        cloned.metadata ??= {}
        cloned.tasks ??= []
        cloned.legislation ??= []
        indexAuthoringTasksInSchedule(cloned)

        const editableDraft = buildScheduleForEditing(cloned, macroMap)

        setDraft(editableDraft)
        setSaveReady(false)
        setOriginalTasks(editableDraft.tasks ?? [])
    }

    function enableSaving() {
        if (preview || !draft) return
        setSaveReady(true)
    }

    function pushUndo(action) {
        setUndoStack((stack) => [...stack, action])
    }

    async function handleUndo() {
        const stack = undoStackRef.current
        if (stack.length === 0) return
        const action = stack[stack.length - 1]
        setUndoStack(stack.slice(0, -1))

        if (action.type === SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_TASK_WITH_AMENDS) {
            await restoreDeletedTaskWithAmends(action)
            return
        }

        if (action.type === SCHEDULE_EDITOR_ACTION_RESTORE_DRAFT_SNAPSHOT) {
            restoreDraftSnapshot(action)
            return
        }

        if (action.type === SCHEDULE_EDITOR_ACTION_RESTORE_RECOMMENDATION_APPLICATION) {
            await restoreRecommendationApplication(action)
            return
        }

        if (action.type === SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_LEGISLATION) {
            restoreDeletedLegislation(action)
            return
        }

        if (action.type === SCHEDULE_EDITOR_ACTION_DELETE_TASK) {
            restoreDeletedTask(action)
        }
    }

    function onPreview() {
        navigateSchedulePreview({ projectId, scheduleId })
    }

    function onEdit() {
        navigateScheduleEditor({ projectId, scheduleId })
    }

    function handleRetrySave() {
        onChange.flush()
    }

    function handleComponentTokenInserted(componentId) {
        componentTokenTargetRef.current?.onComponentTokenInserted?.(componentId)
    }

    function registerComponentTokenTarget(target) {
        componentTokenTargetRef.current = target ?? null
    }

    function clearComponentTokenTarget() {
        componentTokenTargetRef.current = null
    }

    async function saveDraft(currentDraft) {
        if (previewRef.current || !currentDraft) return true

        const pendingScopes = peekSavePrepScopes()
        if (initialDraft?.status === DraftStatus.REJECTED && pendingScopes.length === 0 && !isSaveDirtyRef.current) {
            return true
        }

        const saveStart = isScheduleSavePerfEnabled() ? performance.now() : 0
        const changeToFlushMs =
            isScheduleSavePerfEnabled() && lastChangeAtRef.current
                ? Math.round(saveStart - lastChangeAtRef.current)
                : null

        const { macrosLoading: loading, macroMap: currentMacroMap } = macroStateRef.current
        if (loading && draftTokenIdsStateRef.current.tokenIds.length > 0) {
            // Throw so useSave keeps dirty state and retries via exponential backoff.
            // Returning true would falsely clear dirty state, silently dropping edits.
            throw new Error("Deferring save while macro metadata is loading")
        }

        clearSaveConflicts()

        let draftInput = await consumePayload()
        const payloadSource = draftInput ? "consume" : "fallback"
        if (!draftInput) {
            const fallbackStart = isScheduleSavePerfEnabled() ? performance.now() : 0
            draftInput = workerClientRef.current.runSavePrepFallback({
                draft: currentDraft,
                macroMap: currentMacroMap,
                macroMapKey: draftTokenIdsStateRef.current.key,
                scopes: consumeSavePrepScopes(),
            })
            if (isScheduleSavePerfEnabled()) {
                logScheduleSavePerf("saveDraft.fallbackPrep", {
                    durationMs: Math.round(performance.now() - fallbackStart),
                })
            }
        }

        const unknownTokens = findUnknownMacroTokens(draftInput, currentMacroMap)
        if (unknownTokens.length) {
            throwUnknownTokensConflict(unknownTokens)
        }
        clearSaveConflicts()

        try {
            const apiStart = isScheduleSavePerfEnabled() ? performance.now() : 0
            let saved
            try {
                saved = await saveScheduleDraft({ projectId, scheduleId, draft: draftInput })
            } catch (error) {
                if (!isScheduleNotInScopeError(error)) throw error
                errorSnackbar("Schedule not assigned to work package — changes cannot be saved.", {
                    key: "schedule-not-in-scope-save",
                    preventDuplicate: true,
                })
                // Retrying can never succeed once the schedule has left the scope
                return true
            }
            if (!saved) {
                throw new Error("Failed to save schedule")
            }

            if (isScheduleSavePerfEnabled()) {
                logScheduleSavePerf("saveDraft", {
                    payloadSource,
                    changeToFlushMs,
                    apiDurationMs: Math.round(performance.now() - apiStart),
                    totalDurationMs: Math.round(performance.now() - saveStart),
                    projectId,
                    scheduleId,
                })
            }

            await handlePostSaveSubstantiveEdits(currentDraft)
            const savedDraftId = createDraftId(projectId, scheduleId)
            SaveSucceeded(savedDraftId).raise()
            schedulePostSaveAmendsRefresh(savedDraftId)
            RestorePointAdded(savedDraftId).raise()
            MacroTokenValidationFailed.raiseOnce({ scopeId: macroScopeId, tokens: [] })

            if (draftRef.current && !draftRef.current.isDraft) {
                draftRef.current.isDraft = true
            }

            return true
        } finally {
            releaseLock()
        }

        function throwUnknownTokensConflict(tokens) {
            const uniqueTokens = [...new Set(tokens)]
            const nextKey = uniqueTokens.slice().sort().join("|")
            MacroTokenValidationFailed.raiseOnce({ scopeId: macroScopeId, tokens: uniqueTokens })
            if (saveConflictRef.current.invalidTokensKey !== nextKey) {
                errorSnackbar(formatUnknownMacroMessage(uniqueTokens))
            }
            saveConflictRef.current.invalidTokensKey = nextKey
            throw createSaveConflictError()
        }

        function clearSaveConflicts() {
            if (saveConflictRef.current.invalidTokensKey) {
                MacroTokenValidationFailed.raiseOnce({ scopeId: macroScopeId, tokens: [] })
            }
            saveConflictRef.current = { macrosLoading: false, invalidTokensKey: "" }
        }
    }

    function getSavePrepParams() {
        const currentDraft = draftRef.current
        if (!currentDraft) return null
        const { macroMap: currentMacroMap } = macroStateRef.current
        return {
            draft: currentDraft,
            macroMap: currentMacroMap,
            macroMapKey: draftTokenIdsStateRef.current.key,
            scopes: peekSavePrepScopes(),
        }
    }

    function wrapOnChangeWithPrep() {
        wrappedOnChange.flush = rawOnChange.flush
        return wrappedOnChange

        function wrappedOnChange(...args) {
            if (isScheduleSavePerfEnabled()) {
                lastChangeAtRef.current = performance.now()
                logScheduleSavePerf("onChange", { projectId, scheduleId })
            }
            markDirty()
            schedulePrep()
            return rawOnChange(...args)
        }
    }

    function flushDraftBeforeSave() {
        if (!draftRef.current) return true
        return saveDraft(draftRef.current)
    }

    function restoreDeletedTask(action) {
        const currentDraft = draftRef.current
        if (!currentDraft?.tasks) return
        currentDraft.tasks.splice(action.index, 0, action.task)
        indexAuthoringTasksInSchedule(currentDraft)
        undoRestorePendingRef.current = true
        notifyDraftStructureChanged({ resyncSubstantiveEdits: false })
        infoSnackbar(`Restored task "${action.task.title}"`)
    }

    function restoreDeletedLegislation(action) {
        const currentDraft = draftRef.current
        if (!currentDraft) return
        currentDraft.legislation ??= []
        currentDraft.legislation.splice(action.index, 0, action.legislation)
        notifyDraftStructureChanged({ resyncSubstantiveEdits: false })
        infoSnackbar("Restored reference")
    }

    async function restoreDeletedTaskWithAmends(action) {
        const currentDraft = draftRef.current
        if (!currentDraft?.tasks) return

        await revertDeleteTaskAmend(action)

        currentDraft.tasks.splice(action.index, 0, action.task)
        indexAuthoringTasksInSchedule(currentDraft)
        removeRestoredTaskFromCurrentlyDeleted(currentDraft, action.task)
        undoRestorePendingRef.current = true
        notifyDraftStructureChanged({ resyncSubstantiveEdits: false })
        infoSnackbar(`Restored task "${action.task.title}"`)
    }

    async function revertDeleteTaskAmend(action) {
        const amendsKey = action?.amendsKey
        if (!amendsKey || !deleteAmend) return

        const amend = findCurrentAmendByKey(amendsKey)
        if (!amend?.id) return

        await deleteAmend({ id: amend.id, silent: true })
    }

    function findCurrentAmendByKey(amendsKey) {
        const session = sessionRef.current ?? {}
        const sources = [session.authoringAmends, session.persistedAmends, session.inheritedAmends]
        for (const source of sources) {
            const amend = source?.find((entry) => entry?.payload?.amendsKey === amendsKey)
            if (amend) return amend
        }
        return null
    }

    function removeRestoredTaskFromCurrentlyDeleted(draft, task) {
        const taskId = task?.headerTaskId ?? task?.taskId ?? task?.id
        if (!taskId || !Array.isArray(draft?.currentlyDeletedTasks)) return

        draft.currentlyDeletedTasks = draft.currentlyDeletedTasks.filter(
            (candidate) => !taskMatchesIdentifier(candidate, taskId)
        )
    }

    function restoreDraftSnapshot(action) {
        if (!action?.snapshot) return

        if (!draftRef.current) {
            const snapshot = structuredClone(action.snapshot)
            indexAuthoringTasksInSchedule(snapshot)
            setDraft(snapshot)
            draftRef.current = snapshot
        } else {
            restoreDraftSnapshotInPlace(draftRef.current, action.snapshot)
        }
        notifyDraftStructureChanged()
        infoSnackbar("Reverted recommendation changes")
    }

    async function restoreRecommendationApplication(action) {
        restoreDraftSnapshot(action)

        try {
            await restoreRecommendationAmendUndo(action?.amendUndo)
            await restoreProjectBridgeApplied(action?.projectBridgeApplied)
            notifyDraftEdited()
        } catch (error) {
            console.error("Failed to restore recommendation author notes", error)
            errorSnackbar("Failed to fully restore recommendation author notes")
        }
    }

    async function restoreProjectBridgeApplied(projectBridgeApplied) {
        await persistProjectBridgeAppliedTargets({
            importId: projectBridgeApplied?.importId,
            targets: projectBridgeApplied?.targets,
            projectId,
            scheduleId,
            draftId: editorDraftId,
            applied: false,
        })
    }

    async function restoreRecommendationAmendUndo(amendUndo) {
        if (!amendUndo) return

        if (amendUndo.type === RECOMMENDATION_AMEND_UNDO_DELETE) {
            if (!amendUndo.amendId) return
            await deleteAmend({ id: amendUndo.amendId, silent: true })
            return
        }

        if (amendUndo.type === RECOMMENDATION_AMEND_UNDO_RESTORE) {
            if (!amendUndo.amend?.amendsKey) return
            await saveAmend({ ...amendUndo.amend, silent: true })
        }
    }

    async function reloadAuthoringDraftFromServer() {
        const draftRecord = await getScheduleDraft({ projectId, scheduleId })
        if (!draftRecord?.draft) return false

        const cloned = structuredClone(draftRecord.draft)
        cloned.metadata ??= {}
        cloned.tasks ??= []
        cloned.legislation ??= []
        indexAuthoringTasksInSchedule(cloned)

        const editableDraft = buildScheduleForEditing(cloned, macroMap)

        setDraft(editableDraft)
        setSaveReady(false)
        setOriginalTasks(editableDraft.tasks ?? [])
        draftRef.current = editableDraft
        setUndoStack([])
        MacroTokenValidationFailed.raiseOnce({ scopeId: macroScopeId, tokens: [] })
        savePrepScopesRef.current.clear()
        workerClientRef.current?.reset()
        DraftReloaded(createDraftId(projectId, scheduleId)).raise()
        notifyDraftEdited()

        return true
    }

    function notifyDraftEdited(scopes = []) {
        if (!scopes.length) {
            refreshDraftUi()
            return
        }
        raiseDraftEdited(scopes)
    }

    function handleFieldUpdateForDraftEdited(eventName, value, owner) {
        if (preview) return

        const currentDraft = draftRef.current
        if (!currentDraft) return

        const field = fieldPathFromFieldUpdateEvent(eventName)
        const scopes = resolveEditScopesFromFieldUpdate({ field, target: owner, draft: currentDraft })
        if (!scopes.length) return

        for (const scope of scopes) pendingEditScopesRef.current.add(scope)
        trackSavePrepScopes(scopes)
        queueDraftEditedRaise()
    }

    function queueDraftEditedRaise() {
        if (draftEditDebounceRef.current != null) clearTimeout(draftEditDebounceRef.current)
        draftEditDebounceRef.current = setTimeout(flushDraftEditedRaise, DRAFT_EDIT_DEBOUNCE_MS)
    }

    function flushDraftEditedRaise() {
        draftEditDebounceRef.current = null
        const scopes = [...pendingEditScopesRef.current]
        pendingEditScopesRef.current.clear()
        if (!scopes.length) return
        raiseDraftEdited(scopes)
    }

    function raiseDraftEdited(scopes = []) {
        if (!scopes.length) return
        DraftEdited(createDraftId(projectId, scheduleId)).raise({ scopes })
    }

    function cancelPendingDraftEditedRaise() {
        if (draftEditDebounceRef.current != null) {
            clearTimeout(draftEditDebounceRef.current)
            draftEditDebounceRef.current = null
        }
        pendingEditScopesRef.current.clear()
    }

    function notifyDraftStructureChanged({ resyncSubstantiveEdits = true } = {}) {
        cancelPendingDraftEditedRaise()
        markFullSavePrepRequired()
        TaskListRefresh.raise()
        SummaryDataChanged.raiseOnce()
        DraftStructureChanged(createDraftId(projectId, scheduleId)).raise()
        refresh()

        if (resyncSubstantiveEdits) {
            setHasSubstantiveEdits(null)
            queueSubstantiveEditsResync()
            return
        }

        applyMainThreadSubstantiveEditsCompare()
        if (hasSubstantiveEditsRef.current !== false && scheduleTaskCountMatchesCompareSource()) {
            setHasSubstantiveEdits(false)
        }
        cancelSubstantiveEditsResync()
    }

    function scheduleTaskCountMatchesCompareSource() {
        const draftTaskCount = draftRef.current?.tasks?.length
        const sourceTaskCount = compareStateRef.current.compareSource?.tasks?.length
        return draftTaskCount != null && draftTaskCount === sourceTaskCount
    }

    function setHasSubstantiveEdits(value) {
        if (hasSubstantiveEditsRef.current === value) return
        hasSubstantiveEditsRef.current = value
        if (sessionRef.current) {
            sessionRef.current.hasSubstantiveEdits = value
        }
        SubstantiveEditsChanged(createDraftId(projectId, scheduleId)).raise()
    }

    function handleSaveDirtyChanged(value) {
        const dirty = resolveSaveDirtyFromToBeSaved(value)
        if (isSaveDirtyRef.current === dirty) return
        isSaveDirtyRef.current = dirty
        if (sessionRef.current) {
            sessionRef.current.isSaveDirty = dirty
        }
        SaveDirtyChanged(editorDraftId).raise()
    }

    function resolveSaveDirtyFromToBeSaved(value) {
        if (value instanceof Error && value.message.startsWith("Deferring")) return false
        if (value instanceof Error) return true
        if (value === "queued") return true
        return Boolean(value)
    }

    function scheduleSubstantiveEditsCompareOnIdle() {
        if (substantiveCompareIdleRef.current != null) {
            cancelIdle(substantiveCompareIdleRef.current)
        }

        substantiveCompareIdleRef.current = scheduleIdle(invokeCurrentCompare)

        return cancelScheduledSubstantiveEditsCompare

        function invokeCurrentCompare() {
            runSubstantiveEditsCompareRef.current?.()
        }
    }

    function cancelScheduledSubstantiveEditsCompare() {
        if (substantiveCompareIdleRef.current == null) return
        cancelIdle(substantiveCompareIdleRef.current)
        substantiveCompareIdleRef.current = null
    }

    function runSubstantiveEditsCompareFromIdle() {
        substantiveCompareIdleRef.current = null
        syncSubstantiveEditsFromCompare()
    }

    function warmWorkerWhenCompareSourceReady() {
        if (isNewSchedule || compareLoading || !compareSource) return
        workerClientRef.current?.warm()
    }

    function cleanupWorkerClient() {
        return () => {
            releaseAuthoringScheduleWorkerClient(editorDraftId)
            workerClientRef.current = null
        }
    }

    async function handleSaveSkippedForSubstantiveEdits() {
        if (hasSubstantiveEditsRef.current === true) return
        await reconcileSubstantiveEditsAfterSave({ afterSave: true })
    }

    function handleDraftReloadedForSubstantiveEdits() {
        hasSubstantiveEditsRef.current = null
        syncSubstantiveEditsFromCompare()
        queueSubstantiveEditsResync()
    }

    async function handlePostSaveSubstantiveEdits(savedDraft) {
        await reconcileSubstantiveEditsAfterSave({ draftOverride: savedDraft, afterSave: true })
    }

    async function reconcileSubstantiveEditsAfterSave({ draftOverride, afterSave = false } = {}) {
        let synced = await syncSubstantiveEditsFromCompare({ draftOverride, afterSave })

        if (afterSave && (!synced || hasSubstantiveEditsRef.current === null)) {
            synced = applyMainThreadSubstantiveEditsCompare(draftOverride) || synced
        }

        if (undoRestorePendingRef.current) {
            undoRestorePendingRef.current = false
            setHasSubstantiveEdits(false)
            cancelSubstantiveEditsResync()
            return
        }

        // When compare cannot run yet (no source / still loading), keep submit enabled until resync.
        if (!synced && hasSubstantiveEditsRef.current === false) {
            setHasSubstantiveEdits(true)
            queueSubstantiveEditsResync()
            return
        }

        if (synced && hasSubstantiveEditsRef.current === false) {
            cancelSubstantiveEditsResync()
            return
        }

        queueSubstantiveEditsResync()
    }

    function applyMainThreadSubstantiveEditsCompare(draftOverride) {
        const draftForCompare = draftOverride ?? draftRef.current
        const { compareSource: currentCompareSource } = compareStateRef.current
        const { macroMap: currentMacroMap } = macroStateRef.current
        if (!draftForCompare || !currentCompareSource) return false

        const result = resolveHasCompareChanges({
            draft: draftForCompare,
            compareSource: currentCompareSource,
            compareLoading: false,
            macroMap: currentMacroMap,
            normalizers: getMainThreadAuthoringNormalizers(),
        })
        if (result === null) return false

        setHasSubstantiveEdits(result)
        return true
    }

    async function syncSubstantiveEditsFromCompare({ draftOverride, afterSave = false } = {}) {
        if (isNewSchedule) return false

        const { macrosLoading: loading, macroMap: currentMacroMap } = macroStateRef.current
        const { compareLoading: currentCompareLoading, compareSource: currentCompareSource } = compareStateRef.current
        if (!afterSave && loading && draftTokenIdsStateRef.current.tokenIds.length > 0) return false

        const draftForCompare = draftOverride ?? draftRef.current
        const effectiveCompareLoading = afterSave ? false : currentCompareLoading

        if (effectiveCompareLoading || !draftForCompare || !currentCompareSource) {
            return false
        }

        if (applyMainThreadSubstantiveEditsCompare(draftOverride)) {
            return true
        }

        const requestId = ++compareRequestRef.current
        const jobId = workerClientRef.current.scheduleCompare(
            {
                draft: draftForCompare,
                compareSource: currentCompareSource,
                macroMap: currentMacroMap,
            },
            { priority: afterSave }
        )

        let result = await workerClientRef.current.waitForCompare(jobId, { priority: afterSave })
        result = resolveCompareResultOnMainThread(result, {
            draft: draftForCompare,
            compareSource: currentCompareSource,
            compareLoading: effectiveCompareLoading,
            macroMap: currentMacroMap,
        })

        if (requestId !== compareRequestRef.current) return false
        if (result === undefined) return false
        if (result === null) return false

        setHasSubstantiveEdits(result)
        return true
    }

    function resolveCompareResultOnMainThread(
        result,
        { draft, compareSource, compareLoading: __compareLoading, macroMap }
    ) {
        const mainThreadParams = {
            draft,
            compareSource,
            compareLoading: false,
            macroMap,
            dirty: false,
            normalizers: getMainThreadAuthoringNormalizers(),
        }

        if (result === null || result === undefined) {
            return resolveHasCompareChanges(mainThreadParams)
        }

        if (result === false) {
            const verified = resolveHasCompareChanges(mainThreadParams)
            if (verified === true) return true
        }

        if (result === true) {
            const verified = resolveHasCompareChanges(mainThreadParams)
            if (verified === false) return false
        }

        return result
    }

    function schedulePostSaveAmendsRefresh(draftId) {
        // Server sync-authoring-amends-comparisons debounces after draft put.
        setTimeout(() => AmendsChanged(draftId).raise(), 800)
    }

    function queueSubstantiveEditsResync() {
        if (substantiveEditsResyncTimerRef.current != null) {
            clearTimeout(substantiveEditsResyncTimerRef.current)
        }
        substantiveEditsResyncTimerRef.current = setTimeout(finishSubstantiveEditsResync, 400)
    }

    function cancelSubstantiveEditsResync() {
        if (substantiveEditsResyncTimerRef.current == null) return
        clearTimeout(substantiveEditsResyncTimerRef.current)
        substantiveEditsResyncTimerRef.current = null
    }

    function finishSubstantiveEditsResync() {
        substantiveEditsResyncTimerRef.current = null
        syncSubstantiveEditsFromCompare()
    }

    function trackSavePrepScopes(scopes = []) {
        for (const scope of scopes) savePrepScopesRef.current.add(scope)
    }

    function markFullSavePrepRequired() {
        savePrepScopesRef.current.add(SAVE_PREP_FORCE_FULL_SCOPE)
        workerClientRef.current?.reset()
    }

    function peekSavePrepScopes() {
        return [...savePrepScopesRef.current]
    }

    function consumeSavePrepScopes() {
        const scopes = peekSavePrepScopes()
        savePrepScopesRef.current.clear()
        return scopes
    }
}

function isScheduleNotInScopeError(error) {
    const messages = [error?.message, ...(error?.graphQLErrors ?? []).map((graphQLError) => graphQLError?.message)]
    return messages.some((message) => `${message}`.includes("not in the scope of project"))
}
